'use client';

import type { InteractionType } from '~/model/InteractionType';
import type { ProjectType } from '~/model/ProjectType';
import { VpButton, VpIcon } from '@vtmn-play/react/headless';
import { useTranslations } from 'next-intl';
import { MainCta } from '@/app/[locale]/(entity)/components/interaction/MainCta/MainCta';
import { GuestModeAccess } from '@/app/components/GuestMode/GuestModeAccess';
import { useAuthenticatedContext } from '@/app/components/Provider';
import { useGuest } from '@/app/hooks/useGuest';
import { useRouter } from '@/i18n/routing';
import { checkIsSurvey } from '~/components/Interaction/utils';
import { isUserAdmin } from '~/components/Profile/utils';
import { getRouteFromType } from '~/utils/utils';
import { InteractionMedia } from './InteractionMedia';

export const InteractionAside = ({
  interaction,
  project,
}: {
  interaction: InteractionType;
  project: ProjectType;
}) => {
  const t = useTranslations('FORM');
  const router = useRouter();

  const { user, isAuthenticated, redirectToLogin } = useAuthenticatedContext();
  const { allowed, isGuestModeAllowed, isModalOpened, setIsModalOpened, init } = useGuest(false, interaction?.id);

  const isAdmin = isUserAdmin(user);
  const isSurvey = checkIsSurvey(interaction);
  const interactionRoute = getRouteFromType(interaction['@type']);
  const formUri = `/${interactionRoute}/${interaction.id}`;

  const displayResult = isAdmin
    && isSurvey
    && interaction.result_visible_on_front
    && interaction?.contributors_count > 0;

  const handleGuestAccess = async () => {
    await init(interaction.id);
  };

  const handleParticipate = async () => {
    if (isAuthenticated) {
      return router.push(formUri);
    }

    try {
      const allowedStatus = checkIsSurvey(interaction) && await isGuestModeAllowed(interaction.id);
      if (!allowedStatus) {
        await redirectToLogin(formUri);
      }
    } catch {
      await redirectToLogin(formUri);
    }
  };

  return (
    <div className="interaction-aside">
      <div className="interaction-aside__shortcuts">
        <InteractionMedia interaction={interaction} project={project} />
        <footer className="interaction-aside__actions">
          <MainCta
            interaction={interaction}
            handleParticipate={handleParticipate}
            isSticky
          />
          {allowed && (
            <GuestModeAccess
              isOpen={isModalOpened}
              route={formUri}
              handleClose={() => setIsModalOpened(false)}
              handleOpen={() => setIsModalOpened(true)}
              onAccessAsGuest={handleGuestAccess}
            />
          )}
          {displayResult && (
            <VpButton
              data-testid="admin-button"
              shape="squared"
              onClick={() => {
                router.push(`${formUri}/results`);
              }}
              endSlot={<VpIcon name="lock" />}
              variant="tertiary"
            >
              {t('SEE_RESULTS')}
            </VpButton>
          )}
        </footer>
      </div>
    </div>
  );
};
